// root's panel (D-057, D-060): one panel for every way in (the top-bar root head, the strolling root,
// the Do with root button). Only root and the chat.
//  - Header: root's name and whether AI is connected; New chat once there is a conversation.
//  - Greeting: root waves on a soft brand glow under "Hi {store}, how can I help you?", with a line on
//    what to ask and four starter questions (one tap sends).
//  - Chat: on the first message root slides into the top-right corner and floats there over the
//    conversation (the list fades out under it); root thinks while a reply is on its way and talks
//    while it arrives; each reply sits beside root head.
//  - Composer (drawer footer): Enter sends, Shift + Enter for a new line; below it either the
//    not-connected note or a short accuracy note.
// Replies come from POST …/ai/chat (rootChat.ts); until AI is connected root says so in the chat.
import { t } from "@ady/i18n";
import { Button, cx, Drawer, Icon, type IconName } from "@ady/ui";
import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import { LazyAdyvanceMascot } from "../ai-mascot";
import { mascotStateFor, useAssistantSession } from "../ai-mascot/assistantSession";
import type { MascotState } from "../ai-mascot/mascot.types";
import { RootHead } from "../ai-mascot/RootHead";
import { useShell } from "./context";
import {
  clearRootChat,
  sendToRoot,
  useRootAvailability,
  useRootChat,
  type RootMessage,
} from "./rootChat";

/** Waving when the panel opens (greeting) and when it reopens on a chat. */
const WAVE_MS = 4000;
const WAVE_AGAIN_MS = 2400;
/** Docked root's height in the top-right corner (px). */
const DOCK_H = 84;

const PROMPTS: { key: "orders" | "product" | "delivery" | "whatsapp"; icon: IconName }[] = [
  { key: "orders", icon: "orders" },
  { key: "product", icon: "box" },
  { key: "delivery", icon: "truck" },
  { key: "whatsapp", icon: "chat" },
];

export function RootDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={<RootTitle />}
      actions={<NewChatButton />}
      footer={<Composer />}
    >
      <RootChat />
    </Drawer>
  );
}

function RootTitle() {
  // The API takes ids; the slugs in the address bar 404 (the mock accepted both).
  const { membership } = useShell();
  const ai = useRootAvailability(membership.org.id);
  return (
    <span className="flex flex-col">
      <span>{t("shell.assistant")}</span>
      <span className="flex items-center gap-1.5 text-xs font-normal text-fg-secondary">
        <span
          aria-hidden="true"
          className={cx(
            "size-1.5 flex-none rounded-full",
            ai === "available"
              ? "bg-success-fg"
              : ai === "activation_required"
                ? "bg-warning-fg"
                : "bg-fg-tertiary",
          )}
        />
        {t("aiMascot.chat.assistant")}
        {ai === "unknown" ? null : (
          <>
            {" · "}
            {ai === "available"
              ? t("aiMascot.chat.status.online")
              : t("aiMascot.chat.status.offline")}
          </>
        )}
      </span>
    </span>
  );
}

function NewChatButton() {
  const { membership, store } = useShell();
  const chat = useRootChat(membership.org.id, store.id);
  if (!chat.messages.length) return null;
  return (
    <Button
      variant="ghost"
      size="md"
      iconOnly
      icon="plus"
      onClick={clearRootChat}
      disabled={chat.pending}
      title={t("aiMascot.chat.newChat")}
      className="text-fg-secondary"
    >
      {t("aiMascot.chat.newChat")}
    </Button>
  );
}

function useBoxSize(ref: RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

function RootChat() {
  const { membership, store } = useShell();
  const chat = useRootChat(membership.org.id, store.id);
  const session = useAssistantSession();
  const docked = chat.messages.length > 0;
  const box = useRef<HTMLDivElement>(null);
  const slot = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const { w, h } = useBoxSize(box);

  // root's mood: a wave on open (and after New chat), thinking while a reply is on its way, talking
  // while it arrives; otherwise the live assistant session decides.
  const [mood, setMood] = useState<MascotState | null>("waving");
  const wasDocked = useRef(docked);
  useEffect(() => {
    const id = window.setTimeout(() => setMood(null), docked ? WAVE_AGAIN_MS : WAVE_MS);
    return () => window.clearTimeout(id);
  }, []);
  useEffect(() => {
    // New chat: back to the greeting, wave again.
    if (wasDocked.current && !docked) {
      wasDocked.current = docked;
      setMood("waving");
      const id = window.setTimeout(() => setMood(null), WAVE_MS);
      return () => window.clearTimeout(id);
    }
    wasDocked.current = docked;
    return undefined;
  }, [docked]);
  const last = chat.messages[chat.messages.length - 1];
  const lastId = last?.id;
  useEffect(() => {
    if (chat.pending) {
      setMood("thinking");
      return;
    }
    if (!last || last.role !== "root") return;
    setMood("talking");
    const id = window.setTimeout(
      () => setMood(null),
      Math.min(5000, Math.max(1500, last.text.length * 45)),
    );
    return () => window.clearTimeout(id);
  }, [chat.pending, lastId]);
  const state = mood ?? mascotStateFor(session.status);

  // Keep the newest message in view.
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.messages.length, chat.pending]);

  // root's greeting size follows the panel; the greeting layout leaves a slot for it, and root is
  // moved over that slot (or into the corner) with a transform so it can glide between the two.
  const tall = Math.round(Math.min(240, Math.max(150, h * 0.4)));
  const wide = (tall * 3) / 4;
  const [at, setAt] = useState({ x: 0, y: 0 });
  useLayoutEffect(() => {
    const el = slot.current;
    if (el) setAt({ x: el.offsetLeft, y: el.offsetTop });
  }, [w, h, docked, tall]);
  const scale = DOCK_H / tall;
  const stage: CSSProperties = docked
    ? { transform: `translate(${w - wide * scale}px, 0px) scale(${scale})` }
    : { transform: `translate(${at.x}px, ${at.y}px) scale(1)` };

  return (
    <div ref={box} className="relative h-full min-h-96">
      {w > 0 ? (
        <div
          className="ady-root-stage pointer-events-none absolute top-0 left-0 z-10"
          style={stage}
        >
          <Suspense fallback={<span className="block" style={{ height: tall, width: wide }} />}>
            <LazyAdyvanceMascot state={state} size={tall} speech={session.speech} />
          </Suspense>
        </div>
      ) : null}

      {docked ? (
        <ol
          ref={list}
          aria-label={t("aiMascot.chat.log")}
          aria-live="polite"
          className="ady-root-fade ady-scroll-quiet flex h-full flex-col gap-3 overflow-x-hidden overflow-y-auto pb-1"
          style={{ paddingTop: DOCK_H + 8 }}
        >
          {chat.messages.map((m) => (
            <Message key={m.id} m={m} />
          ))}
          {chat.pending ? (
            <li className="ady-root-msg flex items-end gap-2">
              <RootHead size={24} />
              <span
                className="ady-root-dots flex items-center gap-1 rounded-lg rounded-es-xs border border-card bg-surface px-3 py-3"
                role="status"
                aria-label={t("aiMascot.chat.waiting")}
              >
                <span />
                <span />
                <span />
              </span>
            </li>
          ) : null}
        </ol>
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-4 py-2">
          <p
            className="ady-root-greet relative max-w-full rounded-lg border border-card bg-surface px-4 py-2 text-center text-md font-medium shadow-card"
            role="status"
          >
            {t("aiMascot.chat.greeting", { store: store.name })}
          </p>
          {/* root stands here (the stage above is moved onto this slot), on a soft glow. */}
          <div ref={slot} className="relative flex-none" style={{ height: tall, width: wide }}>
            <span
              aria-hidden="true"
              className="ady-root-halo absolute inset-x-[-30%] top-[12%] bottom-0"
            />
          </div>
          <p className="max-w-80 text-center text-sm text-fg-secondary">
            {t("aiMascot.chat.intro")}
          </p>
          <section className="flex w-full flex-col gap-2" aria-label={t("aiMascot.chat.tryAsking")}>
            <h3 className="text-xs font-medium text-fg-tertiary">{t("aiMascot.chat.tryAsking")}</h3>
            <ul className="grid gap-2 sm:grid-cols-2">
              {PROMPTS.map((p, i) => (
                <li
                  key={p.key}
                  className="ady-root-msg"
                  style={{ animationDelay: `${200 + i * 70}ms` }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      void sendToRoot(
                        membership.org.id,
                        store.id,
                        t(`aiMascot.chat.prompts.${p.key}`),
                      )
                    }
                    className="flex min-h-control w-full items-center gap-2 rounded-lg border border-card bg-surface px-3 py-2 text-start text-sm transition-colors hover:border-primary hover:bg-hover"
                  >
                    <Icon name={p.icon} gradient />
                    <span className="flex-1">{t(`aiMascot.chat.prompts.${p.key}`)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}

function Message({ m }: { m: RootMessage }) {
  const mine = m.role === "user";
  return (
    <li className={cx("ady-root-msg flex items-end gap-2", mine && "justify-end ps-10")}>
      {mine ? null : <RootHead size={24} />}
      <span className="sr-only">{mine ? t("aiMascot.chat.you") : t("aiMascot.chat.rootSaid")}</span>
      <span
        data-content={mine ? "user" : undefined}
        className={cx(
          "max-w-[85%] rounded-lg px-3 py-2 text-sm break-words whitespace-pre-wrap",
          mine
            ? "ady-bg-brand rounded-ee-xs text-on-primary"
            : cx(
                "rounded-es-xs border text-fg",
                m.notice ? "border-warning-border bg-warning-bg" : "border-card bg-surface",
              ),
        )}
      >
        {m.notice ? (
          <Icon name="info" className="me-1 inline-block align-text-bottom text-warning-fg" />
        ) : null}
        {m.text}
      </span>
    </li>
  );
}

function Composer() {
  const { membership, store } = useShell();
  const chat = useRootChat(membership.org.id, store.id);
  const ai = useRootAvailability(membership.org.id);
  const [text, setText] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  // Grow with the text, up to about five lines.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text]);
  const ready = text.trim().length > 0 && !chat.pending;
  const send = () => {
    if (!ready) return;
    const body = text.trim();
    setText("");
    void sendToRoot(membership.org.id, store.id, body);
  };
  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      {/* Same frame as the Conversations reply box; the send button is one control tall (33 px) and
          stays at the bottom right as the text grows. */}
      <div className="flex items-end gap-1 rounded-sm border border-control bg-surface p-1 ps-3 transition-colors focus-within:border-link">
        <textarea
          ref={ref}
          rows={1}
          value={text}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={t("aiMascot.chat.placeholder")}
          aria-label={t("aiMascot.chat.placeholder")}
          className="min-h-control flex-1 resize-none bg-transparent py-1.5 text-sm outline-none placeholder:text-fg-tertiary"
        />
        <button
          type="submit"
          disabled={!ready}
          aria-label={t("aiMascot.chat.send")}
          title={t("aiMascot.chat.send")}
          className={cx(
            "flex size-control flex-none items-center justify-center rounded-xs transition-colors",
            ready
              ? "ady-bg-brand text-on-primary"
              : "cursor-not-allowed bg-sunken text-fg-disabled",
          )}
        >
          <Icon name="send" />
        </button>
      </div>
      <p className="flex items-center gap-1 text-2xs text-fg-tertiary">
        {ai === "activation_required" ? (
          <>
            <Icon name="info" className="flex-none text-warning-fg" />
            {t("aiMascot.chat.offlineNote")}
          </>
        ) : (
          <>
            {t("aiMascot.chat.disclaimer")} {t("aiMascot.chat.hint")}
          </>
        )}
      </p>
    </form>
  );
}
